import React, { useEffect, useRef, useState } from 'react';
import styles from './BookingFilterSidebar.module.css';
import CustomDropdown from '../../Components/CustomerDetailsModal/CustomDropdown';
import paymentStyles from '../../Components/CustomerDetailsModal/MultiSelectDropdown.module.css';
import MultiSelectDropdown from '../../Components/CustomerDetailsModal/MultiSelectDropdown';
import CustomDateInput from '../../Components/CustomerDetailsModal/CustomDateInput';
import DropDownIcon from '../../Components/Icons/DropDownIcon';
import { getTherapistList, getClientList, getPaymentMethods } from '../../APICalling/APICalling';
import { useGlobalContext } from '../../Context/GlobalContext';

const STATIC_PAYMENT_METHOD_OPTIONS = [{ value: 'DISCOUNT', label: 'DISCOUNT' }];

const parsePaymentMethodsResponse = (response) => {
  if (!response || typeof response !== 'object') {
    return { ok: false, list: [] };
  }
  if (response.response?.data && typeof response.response.data === 'object') {
    const d = response.response.data;
    return {
      ok: d.success === true,
      list: Array.isArray(d.data) ? d.data : [],
    };
  }
  const d = response.success !== undefined ? response : response.data;
  if (d && typeof d === 'object' && 'success' in d) {
    return {
      ok: d.success === true,
      list: Array.isArray(d.data) ? d.data : [],
    };
  }
  return { ok: false, list: [] };
};

const PaymentMultiSelectDropdown = ({
  options,
  selectedValues = [],
  onChange,
  placeholder = 'Select options',
  disabled = false,
  searchPlaceholder = 'Search...',
  onOpen
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && typeof onOpen === 'function') {
      onOpen();
    }
  }, [isOpen, onOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const focusTimeout = setTimeout(() => {
      searchInputRef.current?.focus();
      searchInputRef.current?.select();
    }, 0);

    return () => clearTimeout(focusTimeout);
  }, [isOpen]);

  const filteredOptions = options.filter((o) =>
    o.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const displayText =
    selectedValues.length > 0 ? `${selectedValues.length} selected` : placeholder;

  const toggle = () => {
    if (!disabled) setIsOpen((v) => !v);
  };

  const toggleValue = (value) => {
    const next = selectedValues.includes(value)
      ? selectedValues.filter((v) => v !== value)
      : [...selectedValues, value];
    onChange(next);
  };

  return (
    <div className={paymentStyles.dropdownContainer} ref={dropdownRef}>
      <div
        className={`${paymentStyles.dropdownTrigger} ${disabled ? paymentStyles.disabled : ''}`}
        onClick={toggle}
      >
        <span className={paymentStyles.selectedValue}>{displayText}</span>
        <DropDownIcon 
          size={12} 
          color="#6b7280" 
          className={`${paymentStyles.dropdownIcon} ${isOpen ? paymentStyles.rotated : ''}`}
        />
      </div>
      {isOpen && (
        <div className={paymentStyles.dropdownMenu}>
          <div className={paymentStyles.searchContainer}>
            <input
              ref={searchInputRef}
              type="text"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={paymentStyles.searchInput}
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
          </div>
          <div className={paymentStyles.optionsList}>
            {filteredOptions.map((option) => (
              <div 
                key={option.value} 
                className={paymentStyles.dropdownOption}
                onClick={() => toggleValue(option.value)}
              >
                <label className="checkbox" style={{ width: 'auto' }} onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectedValues.includes(option.value)}
                    onChange={() => toggleValue(option.value)}
                    className={paymentStyles.hiddenCheckbox}
                  />
                  <span className="checkmark icn check" />
                </label>
                <span className={paymentStyles.optionLabel}>{option.label}</span>
              </div>
            ))}
          </div>
          {filteredOptions.length === 0 && (
            <div className={paymentStyles.noResults}>No results found</div>
          )}
        </div>
      )}
    </div>
  );
};

const BookingFilterSidebar = ({ isOpen, onClose, onApply, currentFilters = {} }) => {
  const { selectedOutlet, loggedOutletID } = useGlobalContext();
  const outletId = selectedOutlet || loggedOutletID;
  
  const [filters, setFilters] = useState({
    saleAt: '',
    amountFrom: '',
    amountTo: '',
    status: 'All',
    paymentMethods: [],
    saleDate: 'All Time',
    saleDateStart: '',
    saleDateEnd: '',
    includeStaff: [],
    includeStaffEmpty: false,
    clientSearch: '',
    user_id: ''
  });

  const [dateError, setDateError] = useState('');
  const [includeStaffTouched, setIncludeStaffTouched] = useState(false);

  const contentRef = useRef(null);
  const [therapists, setTherapists] = useState([]);
  const [loadingTherapists, setLoadingTherapists] = useState(false);
  const [clientSuggestions, setClientSuggestions] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [paymentMethodOptions, setPaymentMethodOptions] = useState(STATIC_PAYMENT_METHOD_OPTIONS);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false);
  // Helper to scroll the content to bottom smoothly (used in multiple onOpen handlers)
  const scrollToBottomSmooth = () => {
    setTimeout(() => {
      if (contentRef.current) {
        contentRef.current.scrollTo({ top: contentRef.current.scrollHeight, behavior: 'smooth' });
      }
    }, 0);
  };
  const clientDebounceRef = useRef(null);
  const clientDropdownRef = useRef(null);
  const saleInputRef = useRef(null);

  const handleChange = (name, value) => {
    setFilters((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'clientSearch') {
        next.user_id = '';
      }
      if (name === 'includeStaff') {
        setIncludeStaffTouched(true);
        next.includeStaffEmpty = Array.isArray(value) ? value.length === 0 : false;
      }
      if (name === 'saleDateStart' || name === 'saleDateEnd') {
        if (next.saleDateStart && next.saleDateEnd) {
          const start = new Date(next.saleDateStart);
          const end = new Date(next.saleDateEnd);
          if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime()) && start > end) {
            setDateError('Start date cannot be after end date.');
          } else {
            setDateError('');
          }
        } else {
          setDateError('');
        }
      }
      return next;
    });
  };

  const fetchClientSuggestions = async (text) => {
    if (!text || String(text).trim() === '') {
      setClientSuggestions([]);
      setShowClientDropdown(false);
      return;
    }
    try {
      setLoadingClients(true);
      const params = {
        pagination: 1,
        page: 1,
        search_text: String(text).trim()
      };
      const response = await getClientList(params);
      const list = response?.data?.data?.list?.clients || [];
      setClientSuggestions(list);
      setShowClientDropdown(true);
      // Wait for dropdown and all client items to render before scrolling
      setTimeout(() => {
        if (contentRef.current) {
          contentRef.current.scrollTo({ top: contentRef.current.scrollHeight, behavior: 'smooth' });
        }
      }, 150);
    } catch (e) {
      setClientSuggestions([]);
      setShowClientDropdown(false);
    } finally {
      setLoadingClients(false);
    }
  };

  const onClientInputChange = (e) => {
    const val = e.target.value;
    handleChange('clientSearch', val);
    if (clientDebounceRef.current) clearTimeout(clientDebounceRef.current);
    clientDebounceRef.current = setTimeout(() => {
      fetchClientSuggestions(val);
    }, 400);
  };

  const handleClientSelect = (client) => {
    const label = [client?.name, client?.lastname].filter(Boolean).join(' ');
    setFilters((prev) => ({ ...prev, clientSearch: label, user_id: client?.id }));
    setShowClientDropdown(false);
  };

  const handleClearAll = () => {
    // Set includeStaff to all therapists when clearing (default state)
    const allTherapistIds = therapists.map(t => t.id);
    const cleared = {
      saleAt: '',
      amountFrom: '',
      amountTo: '',
      status: 'All',
      paymentMethods: [],
      saleDate: 'All Time',
      saleDateStart: '',
      saleDateEnd: '',
      includeStaff: allTherapistIds,
      includeStaffEmpty: false,
      clientSearch: '',
      user_id: ''
    };
    setFilters(cleared);
    setDateError('');
    setIncludeStaffTouched(false);
    // Don't send includeStaff when all are selected (treat as no filter)
    const clearedForAPI = { ...cleared, includeStaff: [] };
    onApply?.(clearedForAPI, { keepOpen: true });
    // Add delay to ensure state updates before API call

  };

  const handleApply = () => {
    // Validate date range when using Custom Time Period
    if (filters.saleDate === 'Custom Time Period') {
      // Check if both dates are filled
      if (!filters.saleDateStart || !filters.saleDateEnd) {
        setDateError('Both start date and end date are required.');
        return;
      }
      // Check if start date is after end date
      const start = new Date(filters.saleDateStart);
      const end = new Date(filters.saleDateEnd);
      if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime()) && start > end) {
        setDateError('Start date cannot be after end date.');
        return;
      }
      // Clear error if validation passes
      setDateError('');
    }
    const processed = { ...filters };
    if (processed.saleDateStart) {
      const sd = new Date(processed.saleDateStart);
      processed.saleDateStart = sd.toLocaleDateString('en-GB').split('/').reverse().join('-');
    }
    if (processed.saleDateEnd) {
      const ed = new Date(processed.saleDateEnd);
      processed.saleDateEnd = ed.toLocaleDateString('en-GB').split('/').reverse().join('-');
    }
    // If all therapists are selected, don't send includeStaff (treat as no filter)
    const allTherapistIds = therapists.map(t => t.id);
    const isAllTherapistsSelected = therapists.length > 0 && 
      processed.includeStaff.length === therapists.length &&
      allTherapistIds.every(id => processed.includeStaff.includes(id));
    if (isAllTherapistsSelected) {
      processed.includeStaff = [];
      processed.includeStaffEmpty = false;
    } else if (includeStaffTouched && processed.includeStaff.length === 0) {
      // User explicitly deselected all therapists
      processed.includeStaffEmpty = true;
    }
    onApply?.(processed, { keepOpen: false });
    setIncludeStaffTouched(false);
    onClose?.();
  };

  const hasActiveFilters = () => {
    if (includeStaffTouched) return true;
    if (filters.includeStaffEmpty) return true;
    if (filters.saleAt && String(filters.saleAt).trim() !== '') return true;
    if (filters.amountFrom && String(filters.amountFrom).trim() !== '') return true;
    if (filters.amountTo && String(filters.amountTo).trim() !== '') return true;
    if (filters.status && filters.status !== 'All') return true;
    if (Array.isArray(filters.paymentMethods) && filters.paymentMethods.length > 0) return true;
    if (filters.saleDate && filters.saleDate !== 'All Time') return true;
    // Only count includeStaff as active if not all therapists are selected
    const allTherapistIds = therapists.map(t => t.id);
    const isAllTherapistsSelected = therapists.length > 0 && 
      filters.includeStaff.length === therapists.length &&
      allTherapistIds.every(id => filters.includeStaff.includes(id));
    if (Array.isArray(filters.includeStaff) && filters.includeStaff.length > 0 && !isAllTherapistsSelected) return true;
    if (filters.user_id && String(filters.user_id).trim() !== '') return true;
    return false;
  };
  const getCurrentDateInfo = () => {
    const now = new Date();
    const today = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

    const thisWeekStart = new Date(now);
    const dayOfWeek = now.getDay();
    const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    thisWeekStart.setDate(now.getDate() - daysFromMonday);
    const thisWeekEnd = new Date(thisWeekStart);
    thisWeekEnd.setDate(thisWeekStart.getDate() + 6);
    const thisWeek = `${thisWeekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${thisWeekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;

    const lastWeekStart = new Date(thisWeekStart);
    lastWeekStart.setDate(thisWeekStart.getDate() - 7);
    const lastWeekEnd = new Date(lastWeekStart);
    lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
    const lastWeek = `${lastWeekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${lastWeekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;

    const twoWeeksAgoStart = new Date(lastWeekStart);
    twoWeeksAgoStart.setDate(lastWeekStart.getDate() - 7);
    const lastTwoWeeks = `${twoWeeksAgoStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${lastWeekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;

    const currentMonth = now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    const previousMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    const twoMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 2, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

    return { today, yesterday, thisWeek, lastWeek, lastTwoWeeks, currentMonth, previousMonth, twoMonthsAgo };
  };

  useEffect(() => {
    if (isOpen) {
      setIncludeStaffTouched(false);
    }
    const fetchTherapists = async () => {
      if (!selectedOutlet) return;
      
      try {
        setLoadingTherapists(true);
        const response = await getTherapistList({ 
          outlet: selectedOutlet,
          pagination: 0, 
          page: 1 
        });
        if (response?.data) {
          const allStaff = response.data?.data?.data?.list?.staffs || [];
          const therapistsOnly = allStaff.filter((staff) => {
            const role = (staff.role || '').toString().toLowerCase();
            const isReception = role.includes('receptionist');
            const isStaff = role ? role.includes('staff') : true; // assume therapist when role missing
            const isActive = Number(staff.status) === 1 || staff.status === '1';
            return isActive && !isReception && isStaff;
          });
          setTherapists(therapistsOnly);
          
          // Set all therapists as default selection if includeStaff is empty
          if (therapistsOnly.length > 0) {
            setFilters(prev => {
              if (prev.includeStaff.length === 0 && !prev.includeStaffEmpty) {
                const allTherapistIds = therapistsOnly.map(t => t.id);
                return { ...prev, includeStaff: allTherapistIds };
              }
              return prev;
            });
          }
        }
      } catch {
        setTherapists([]);
      } finally {
        setLoadingTherapists(false);
      }
    };

    if (!isOpen) return;
    if (therapists.length === 0 && selectedOutlet) {
      fetchTherapists();
    }
  }, [isOpen, therapists.length, selectedOutlet]);

  useEffect(() => {
    if (!isOpen || !outletId) {
      return;
    }

    const fetchPaymentMethodsForFilter = async () => {
      try {
        setLoadingPaymentMethods(true);
        const response = await getPaymentMethods(outletId);
        const { ok, list } = parsePaymentMethodsResponse(response);
        if (ok) {
          const apiOptions = list
            .sort((a, b) => Number(a.ranking ?? 0) - Number(b.ranking ?? 0))
            .map((method) => ({
              value: method.title,
              label: method.title,
            }));
          setPaymentMethodOptions([...apiOptions, ...STATIC_PAYMENT_METHOD_OPTIONS]);
        } else {
          setPaymentMethodOptions(STATIC_PAYMENT_METHOD_OPTIONS);
        }
      } catch {
        setPaymentMethodOptions(STATIC_PAYMENT_METHOD_OPTIONS);
      } finally {
        setLoadingPaymentMethods(false);
      }
    };

    fetchPaymentMethodsForFilter();
  }, [isOpen, outletId]);

  useEffect(() => {
    const handleOutside = (e) => {
      if (clientDropdownRef.current && !clientDropdownRef.current.contains(e.target)) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  // Update filters when sidebar opens with applied filters
  useEffect(() => {
    if (isOpen && currentFilters && Object.keys(currentFilters).length > 0) {
      setFilters(prevFilters => {
        const updated = { ...prevFilters, ...currentFilters };
        // If includeStaff is empty in currentFilters, it means all therapists were selected
        // Set it to all therapist IDs for UI display (only if therapists are loaded)
        if (currentFilters.includeStaffEmpty) {
          updated.includeStaff = [];
        } else if (currentFilters.includeStaff && currentFilters.includeStaff.length === 0 && therapists.length > 0) {
          const allTherapistIds = therapists.map(t => t.id);
          updated.includeStaff = allTherapistIds;
        } else if (!currentFilters.includeStaff && therapists.length > 0 && prevFilters.includeStaff.length === 0 && !prevFilters.includeStaffEmpty) {
          // If includeStaff is not in currentFilters and prevFilters is empty, set to all therapists
          const allTherapistIds = therapists.map(t => t.id);
          updated.includeStaff = allTherapistIds;
        }
        return updated;
      });
    }
  }, [isOpen, therapists.length]); // Depend on therapists.length to handle when therapists are loaded

  useEffect(() => {
    if (!isOpen) return;

    const focusTimeout = setTimeout(() => {
      saleInputRef.current?.focus();
      saleInputRef.current?.select();
    }, 0);

    return () => clearTimeout(focusTimeout);
  }, [isOpen]);


  if (!isOpen) return null;

  return (
    <div className={`${styles.filterOverlay} ${isOpen ? styles.open : ''}`} onClick={onClose}>
      <div className={`${styles.filterSidebar} ${isOpen ? styles.open : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className={styles.filterHeader}>
          <div></div>
          <button className={styles.closeButton} onClick={onClose}>×</button>
        </div>

        <div className={styles.filterContent} ref={contentRef}>
          {/* Sale # */}
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>Filter by sales</label>
            <input
              ref={saleInputRef}
              type="text"
              className={`${styles.textInput} ${styles.placeholderStrong}`}
              placeholder="Sale #"
              value={filters.saleAt}
              onChange={(e) => handleChange('saleAt', e.target.value)}
            />
          </div>

          {/* Amount */}
          <div className={styles.filterGroup}>
            <div className={styles.amountRow}>
              <input className={`${styles.numberInput} ${styles.placeholderStrong}`} type="text" placeholder="From amount" value={filters.amountFrom} onChange={(e) => handleChange('amountFrom', e.target.value)} />
              <input className={`${styles.numberInput} ${styles.placeholderStrong}`} type="text" placeholder="To amount" value={filters.amountTo} onChange={(e) => handleChange('amountTo', e.target.value)} />
            </div>
          </div>

          {/* Status */}
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>Status</label>
            <CustomDropdown
              options={[
                { value: 'Open', label: 'Open' },
                { value: 'Closed', label: 'Closed' },
                { value: 'Open(In Progress)', label: 'Open(In Progress)' },
                { value: 'Refunded', label: 'Refunded' },
              ]}
              value={filters.status}
              onChange={(v) => handleChange('status', v)}
              placeholder="All"
              disableSearch
            />
          </div>

          {/* Sale payment method */}
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>Sale payment method</label>
            <PaymentMultiSelectDropdown
              options={paymentMethodOptions}
              selectedValues={filters.paymentMethods}
              onChange={(vals) => handleChange('paymentMethods', vals)}
              placeholder={loadingPaymentMethods ? 'Loading...' : 'All'}
              disabled={loadingPaymentMethods}
              searchPlaceholder="Search..."
              onOpen={scrollToBottomSmooth}
            />
          </div>

          {/* Sale date */}
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>Sale date</label>
            <CustomDropdown
              options={[
                { value: 'All Time', label: 'All Time' },
                { value: `Today (${getCurrentDateInfo().today})`, label: `Today (${getCurrentDateInfo().today})` },
                { value: `Yesterday (${getCurrentDateInfo().yesterday})`, label: `Yesterday (${getCurrentDateInfo().yesterday})` },
                { value: `This Week (${getCurrentDateInfo().thisWeek})`, label: `This Week (${getCurrentDateInfo().thisWeek})` },
                { value: `Last Week (${getCurrentDateInfo().lastWeek})`, label: `Last Week (${getCurrentDateInfo().lastWeek})` },
                { value: `Last Two Weeks (${getCurrentDateInfo().lastTwoWeeks})`, label: `Last Two Weeks (${getCurrentDateInfo().lastTwoWeeks})` },
                { value: 'Custom Time Period', label: 'Custom Time Period' },
                { value: getCurrentDateInfo().currentMonth, label: getCurrentDateInfo().currentMonth },
                { value: getCurrentDateInfo().previousMonth, label: getCurrentDateInfo().previousMonth },
                { value: getCurrentDateInfo().twoMonthsAgo, label: getCurrentDateInfo().twoMonthsAgo }
              ]}
              value={filters.saleDate}
              onChange={(v) => handleChange('saleDate', v)}
              placeholder="All Time"
            disableSearch
            onOpen={scrollToBottomSmooth}
            />
            {filters.saleDate === 'Custom Time Period' && (
              <>
                <div className={styles.customDateInputs}>
                  <div className={styles.dateInputContainer}>
                    <CustomDateInput value={filters.saleDateStart} onChange={(v) => handleChange('saleDateStart', v)} className={styles.dateInput} placeholder="Start date" />
                  </div>
                  <div className={styles.dateInputContainer}>
                    <CustomDateInput value={filters.saleDateEnd} onChange={(v) => handleChange('saleDateEnd', v)} className={styles.dateInput} placeholder="End date" />
                  </div>
                </div>
                {dateError && (
                  <div style={{ color: '#dc2626', fontSize: 12, marginTop: 6 }}>{dateError}</div>
                )}
              </>
            )}
          </div>

          {/* Includes staff member */}
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>Includes staff member</label>
            <MultiSelectDropdown
              options={Array.isArray(therapists) ? therapists.map(therapist => ({
                value: therapist.id,
                label: therapist.alias || therapist.name || 'Unnamed',
                id: therapist.id,
                gender: therapist.gender,
                isStarred: therapist.alias && therapist.alias.includes('*')
              })) : []}
              selectedValues={filters.includeStaff}
              onChange={(vals) => handleChange('includeStaff', vals)}
              placeholder="Select therapists"
              allSelectedText="All therapists"
              searchPlaceholder="Search therapists..."
              loading={loadingTherapists}
              hasMore={false}
              autoFocusSearch
              onOpen={scrollToBottomSmooth}
            />
          </div>

          {/* Client */}
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>Client</label>
            <div style={{ position: 'relative' }} ref={clientDropdownRef}>
              <input
                type="text"
                className={`${styles.textInput} ${styles.placeholderStrong}`}
                placeholder="Search for a client"
                value={filters.clientSearch}
                onChange={onClientInputChange}
                autoComplete="off"
              />
               {showClientDropdown && (
                 <div className={paymentStyles.dropdownMenu}>
                   {loadingClients ? (
                     <div className={paymentStyles.noResults}>Searching...</div>
                   ) : (
                     <div className={paymentStyles.optionsList}>
                       {clientSuggestions.map((c) => {
                         const name = [c?.name, c?.lastname].filter(Boolean).join(' ');
                         return (
                           <div key={c.id} className={paymentStyles.dropdownOption} onClick={() => handleClientSelect(c)}>
                             <div style={{ display: 'flex', flexDirection: 'column' }}>
                               <span className={paymentStyles.optionLabel}>{name || 'Unnamed'}</span>
                               <span style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{c?.contact_number || c?.email || ''}</span>
                             </div>
                           </div>
                         );
                       })}
                       {clientSuggestions.length === 0 && (
                         <div className={paymentStyles.noResults}>No clients found</div>
                       )}
                     </div>
                   )}
                 </div>
               )}
            </div>
          </div>
        </div>

        <div className={styles.filterActions}>
          <div className={styles.actionButtons}>
            <button 
              className={styles.clearButton} 
              onClick={handleClearAll}
              disabled={!hasActiveFilters()}
              style={{ opacity: !hasActiveFilters() ? 0.5 : 1, cursor: !hasActiveFilters() ? 'not-allowed' : 'pointer' }}
            >
              Clear all
            </button>
            {hasActiveFilters() && (
              <button className={styles.applyButton} onClick={handleApply}>Apply Filter</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingFilterSidebar;


